import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  UserIcon,
  UsersIcon,
  XMarkIcon,
  CheckIcon,
  XCircleIcon,
  EnvelopeIcon,
  KeyIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { useGetUsersQuery, useGetRolesQuery, useAddUserMutation, useUpdateUserMutation, useDeleteUserMutation } from '../store/api';
import ExportButton from '../components/ExportButton';
import PermissionButton from '../components/PermissionButton';
import { toast } from 'react-toastify';
import { Audio } from 'react-loader-spinner';
import TableHeader from '../components/TableHeader';
import { usePermission } from '../hooks/useAuth';

export default function UserManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [consultantFilter, setConsultantFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [usersData, setUsersData] = useState(null);
  const [isExport, setIsExport] = useState(0);
  const [exportData, setExportData] = useState([]);

  // Permission hooks - strictly permission-based
  const canAddUser = usePermission('user_management.add');
  const canEditUser = usePermission('user_management.edit');
  const canDeleteUser = usePermission('user_management.delete');
  const canViewUser = usePermission('user_management.view'); // Already being used in route protection

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role_id: '',
    is_active: true,
    is_consultant: false,
    password: ''
  });

  // Get Users Query
  const { data, isError, error, isSuccess, isLoading } = useGetUsersQuery({
    keyword: searchTerm,
    role_id: roleFilter,
    is_active: statusFilter,
    is_consultant: consultantFilter,
    is_export: isExport,
    page: currentPage,
    limit: itemsPerPage
  });

  // Get Roles for filter
  const { data: rolesData } = useGetRolesQuery({
    keyword: '',
    is_active: '1',
    is_export: 0,
    page: 1,
    limit: 1000
  });

  useEffect(() => {
    if (isExport == 1 && data?.data) {
      const transformedData = data.data.map(user => ({
        'Ad': user.first_name || '',
        'Soyad': user.last_name || '',
        'İstifadəçi adı': user.username || '',
        'Email': user.email || '',
        'Telefon': user.phone || '',
        'Rol': user.role?.name || '',
        'Konsultant': user.is_consultant ? 'Bəli' : 'Xeyr',
        'Status': user.is_active ? 'Aktiv' : 'Deaktiv',
        'Son Giriş': user.last_login || 'Heç vaxt',
        'ID': user.id
      }));
      setExportData(transformedData);
    } else {
      if (!isLoading && data?.data) {
        setUsersData(data.data.data || []);
        setTotalPages(Math.ceil((data.data.total || 0) / (data.data.per_page || 10)));
        setTotalUserCount(data.data.total || 0);
      }
    }
  }, [data, isSuccess, isError, isLoading, error]);

  // Add User Mutation
  const [addUser, {
    data: createResponse,
    isError: createIsError,
    error: createError,
    isSuccess: createIsSuccess,
    isLoading: createIsLoading
  }] = useAddUserMutation();

  useEffect(() => {
    if (!createIsLoading && createResponse) {
      toast.success(createResponse.message);
      setIsModalOpen(false);
      setFormData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        role_id: '',
        is_active: true,
        is_consultant: false,
        password: ''
      });
    }
    if (createIsError) {
      toast.error(createError?.data?.message || 'Əlavə etmə uğursuz oldu');
    }
  }, [createResponse, createIsSuccess, createIsError, createIsLoading, createError]);

  // Update User Mutation
  const [updateUser, {
    data: updateData,
    isError: updateIsError,
    error: updateError,
    isSuccess: updateIsSuccess,
    isLoading: updateIsLoading
  }] = useUpdateUserMutation();

  useEffect(() => {
    if (!updateIsLoading && updateData) {
      toast.success(updateData.message);
      setIsModalOpen(false);
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        role_id: '',
        is_active: true,
        is_consultant: false,
        password: ''
      });
    }
    if (updateIsError) {
      toast.error(updateError?.data?.message || 'Yeniləmə uğursuz oldu');
    }
  }, [updateData, updateIsSuccess, updateIsError, updateIsLoading, updateError]);

  // Delete User Mutation
  const [deleteUser, {
    data: deleteRes,
    isError: deleteIsError,
    error: deleteError,
    isSuccess: deleteIsSuccess,
    isLoading: deleteIsLoading
  }] = useDeleteUserMutation();

  useEffect(() => {
    if (!deleteIsLoading && deleteRes && deleteIsSuccess) {
      toast.success(deleteRes.message);
    }
    if (deleteIsError && deleteError) {
      toast.error('Silmə əməliyyatı uğursuz oldu');
    }
  }, [deleteIsSuccess, deleteIsError, deleteRes, deleteError, deleteIsLoading]);

  // Statistics
  const totalUsers = totalUserCount;
  const activeUsers = usersData?.filter(u => u.is_active).length || 0;
  const inactiveUsers = usersData?.filter(u => !u.is_active).length || 0;
  const consultantUsers = usersData?.filter(u => u.is_consultant).length || 0;
  const startIndex = (currentPage - 1) * itemsPerPage;

  const exportHeaders = [
    'Ad', 'Soyad', 'İstifadəçi adı', 'Email', 'Telefon', 'Rol', 'Konsultant', 'Status', 'Son Giriş', 'ID'
  ];

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      role_id: '',
      is_active: true,
      is_consultant: false,
      password: ''
    });
    setIsModalOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      role_id: user.role_id ? user.role_id.toString() : '',
      is_active: user.is_active !== null && user.is_active !== undefined ? user.is_active : true,
      is_consultant: user.is_consultant !== null && user.is_consultant !== undefined ? user.is_consultant : false,
      password: ''
    });
    setIsModalOpen(true);
  };

  const handleView = (user) => {
    setViewingUser(user);
    setIsViewModalOpen(true);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    setConsultantFilter('');
    setCurrentPage(1);
  };

  const handleDelete = (userId) => {
    if (window.confirm('Bu istifadəçini silmək istədiyinizə əminsiniz?')) {
      deleteUser(userId);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      role_id: parseInt(formData.role_id) || null,
      is_active: formData.is_active ? 1 : 0,
      is_consultant: formData.is_consultant ? 1 : 0
    };

    if (editingUser) {
      updateUser({ id: editingUser.id, obj: submitData });
    } else {
      addUser(submitData);
    }
  };

  const getColorClasses = (role) => {
    const colorMap = {
      'Super Admin': 'bg-red-100 text-red-800 border-red-200',
      'Admin': 'bg-blue-100 text-blue-800 border-blue-200',
      'Konsultant': 'bg-green-100 text-green-800 border-green-200',
      'Mühasib': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Menecer': 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colorMap[role] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">İstifadəçi İdarəetməsi</h1>
          <p className="mt-1 text-sm text-gray-600">
            Sistem istifadəçilərini və onların rollarını idarə edin
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <ExportButton 
            data={exportData}
            filename="istifadeciler"
            columns={exportHeaders}
            isExport={isExport}
            setIsExport={setIsExport}
          />
          {/* Add User button - only show if user has user_management.add permission */}
          <PermissionButton
            permission="user_management.add"
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Yeni İstifadəçi
          </PermissionButton>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UsersIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-blue-100 text-sm font-medium">Ümumi İstifadəçilər</p>
              <p className="text-2xl font-bold">{totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-green-100 text-sm font-medium">Aktiv İstifadəçilər</p>
              <p className="text-2xl font-bold">{activeUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-red-100 text-sm font-medium">Qeyri-aktiv</p>
              <p className="text-2xl font-bold">{inactiveUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-purple-100 text-sm font-medium">Konsultantlar</p>
              <p className="text-2xl font-bold">{consultantUsers}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Ad, email, istifadəçi adı..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün rollar</option>
            {rolesData?.data?.filter(r => r.is_active).map(role => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün statuslar</option>
            <option value="1">Aktiv</option>
            <option value="0">Qeyri-aktiv</option>
          </select>

          <select
            value={consultantFilter}
            onChange={(e) => setConsultantFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün istifadəçilər</option>
            <option value="1">Konsultantlar</option>
            <option value="0">Qeyri-konsultantlar</option>
          </select>

          <button
            onClick={handleClearFilters}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Təmizlə
          </button>
        </div>
      </div>

      {/* Users table */}
      <div className="bg-white shadow-lg overflow-hidden sm:rounded-xl border border-blue-100">
        <TableHeader
          title="İstifadəçilər"
          startIndex={startIndex}
          itemsPerPage={itemsPerPage}
          totalRecordCount={totalUserCount}
          setItemsPerPage={setItemsPerPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          currentPage={currentPage}
        />

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                  İstifadəçi
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                  Əlaqə
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                  Rol
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                  Konsultant
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                  Əməliyyatlar
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {!isLoading && usersData?.map((user) => (
                <tr key={user.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow mr-3">
                        <span className="text-sm font-medium text-white">
                          {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-900 transition-colors">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-xs text-gray-500">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <div className="text-sm text-gray-900">{user.email}</div>
                      </div>
                      {user.phone && (
                        <div className="flex items-center">
                          <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-900">{user.phone}</div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getColorClasses(user.role?.name)}`}>
                      {user.role?.name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.is_consultant 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-gray-100 text-gray-800 border border-gray-200'
                    }`}>
                      {user.is_consultant ? 'Bəli' : 'Xeyr'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.is_active 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                      {user.is_active ? 'Aktiv' : 'Qeyri-aktiv'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex justify-end space-x-2">
                      {/* View button - always visible if user can view user management (already protected by route) */}
                      <button
                        onClick={() => handleView(user)}
                        className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                        title="Ətraflı bax"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      
                      {/* Edit button - only show if user has user_management.edit permission */}
                      <PermissionButton
                        permission="user_management.edit"
                        onClick={() => handleEdit(user)}
                        className="inline-flex items-center p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 rounded-lg transition-all duration-200 hover:scale-105"
                        title="Redaktə et"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </PermissionButton>
                      
                      {/* Delete button - only show if user has user_management.delete permission */}
                      <PermissionButton
                        permission="user_management.delete"
                        onClick={() => handleDelete(user.id)}
                        className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                        title="Sil"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </PermissionButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!isLoading && usersData?.length === 0 && (
          <div className="text-center py-16">
            <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
              <UsersIcon className="h-8 w-8 text-blue-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Heç bir istifadəçi tapılmadı</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              Axtarış şərtlərinizi dəyişin və ya yeni istifadəçi əlavə edin.
            </p>
            {/* Add User button in empty state - only show if user has user_management.add permission */}
            <PermissionButton
              permission="user_management.add"
              onClick={handleAdd}
              className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              İlk İstifadəçini Əlavə Et
            </PermissionButton>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center items-center py-16">
            <Audio
              height="80"
              width="80"
              radius="9"
              color="blue"
              ariaLabel="loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </div>
        )}
      </div>

      {/* Add/Edit User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingUser ? 'İstifadəçini Redaktə Et' : 'Yeni İstifadəçi Əlavə Et'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ad *</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      placeholder="Adı daxil edin..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Soyad *</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      placeholder="Soyadı daxil edin..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">İstifadəçi adı *</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      placeholder="İstifadəçi adını daxil edin..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      placeholder="Email ünvanını daxil edin..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Telefon nömrəsini daxil edin..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rol *</label>
                    <select
                      value={formData.role_id}
                      onChange={(e) => setFormData({...formData, role_id: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Rol seçin</option>
                      {rolesData?.data?.filter(r => r.is_active).map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Şifrə {!editingUser && '*'}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <KeyIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required={!editingUser}
                        placeholder={editingUser ? "Dəyişmək üçün yeni şifrə..." : "Şifrəni daxil edin..."}
                      />
                    </div>
                    {editingUser && (
                      <p className="mt-1 text-xs text-gray-500">
                        Şifrəni dəyişmək istəmirsinizsə boş buraxın
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                      Aktiv istifadəçi
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_consultant"
                      checked={formData.is_consultant}
                      onChange={(e) => setFormData({...formData, is_consultant: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_consultant" className="ml-2 block text-sm text-gray-900">
                      Konsultant
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv Et
                  </button>
                  <button
                    type="submit"
                    disabled={createIsLoading || updateIsLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    {createIsLoading || updateIsLoading ? 'Gözləyin...' : (editingUser ? 'Yenilə' : 'Əlavə Et')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View User Modal */}
      {isViewModalOpen && viewingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">İstifadəçi Məlumatları</h3>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0 h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                    <span className="text-xl font-bold text-white">
                      {viewingUser.first_name?.charAt(0)}{viewingUser.last_name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900">
                      {viewingUser.first_name} {viewingUser.last_name}
                    </h4>
                    <p className="text-sm text-gray-500">@{viewingUser.username}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getColorClasses(viewingUser.role?.name)}`}>
                        {viewingUser.role?.name}
                      </span>
                      {viewingUser.is_consultant && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                          Konsultant
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingUser.email}</p>
                  </div>
                  {viewingUser.phone && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Telefon</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingUser.phone}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Status</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        viewingUser.is_active 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {viewingUser.is_active ? 'Aktiv' : 'Qeyri-aktiv'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Son Giriş</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingUser.last_login ? new Date(viewingUser.last_login).toLocaleString('az-AZ') : 'Heç vaxt'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Yaradılma Tarixi</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingUser.created_at ? new Date(viewingUser.created_at).toLocaleString('az-AZ') : 'Naməlum'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Son Yeniləmə</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingUser.updated_at ? new Date(viewingUser.updated_at).toLocaleString('az-AZ') : 'Naməlum'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Bağla
                </button>
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    handleEdit(viewingUser);
                  }}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Redaktə Et
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}